const BUCKET_PX = 2;
const MIN_SHOULDER_PX = 8;
const MAX_SHOULDER_PX = 12;

export type SummaryRun = { startX: number; endX: number; count: number };

/**
 * Count points in their containing bucket and spans at each bucket's midpoint.
 * Only count changes are stored: memory depends on segment endpoints, not the
 * full video width at the current zoom.
 */
export function buildSummaryRuns(
  segments: readonly { start: number; end?: number | null }[],
  pixelsPerSecond: number,
  durationSeconds: number,
): { runs: SummaryRun[]; maxCount: number; totalWidth: number } {
  const totalWidth = durationSeconds * pixelsPerSecond;
  const numBuckets = Math.max(1, Math.ceil(totalWidth / BUCKET_PX));
  const changes = new Map<number, number>();

  // Search the original midpoint expression rather than rounding a converted
  // timestamp: those expressions differ at floating-point bucket boundaries.
  const firstMidpointAtOrAfter = (time: number): number => {
    let low = 0;
    let high = numBuckets;
    while (low < high) {
      const bucket = low + Math.floor((high - low) / 2);
      const midpoint = (bucket * BUCKET_PX + BUCKET_PX / 2) / pixelsPerSecond;
      if (midpoint < time) low = bucket + 1;
      else high = bucket;
    }
    return low;
  };

  const addRange = (start: number, end: number) => {
    if (start >= end) return;
    changes.set(start, (changes.get(start) ?? 0) + 1);
    changes.set(end, (changes.get(end) ?? 0) - 1);
  };

  for (const segment of segments) {
    const end = segment.end ?? segment.start;
    if (end === segment.start) {
      const bucket = Math.floor((segment.start * pixelsPerSecond) / BUCKET_PX);
      if (bucket >= 0 && bucket < numBuckets) addRange(bucket, bucket + 1);
    } else if (segment.start < end) {
      // Half-open spans exclude a midpoint equal to their end.
      addRange(firstMidpointAtOrAfter(segment.start), firstMidpointAtOrAfter(end));
    }
  }

  const runs: SummaryRun[] = [];
  let count = 0;
  let maxCount = 0;
  let runStart = 0;
  for (const boundary of [...changes.keys()].sort((a, b) => a - b)) {
    const change = changes.get(boundary)!;
    // Adjacent equally dense spans form one run, preserving their smoothing.
    if (change === 0) continue;
    if (boundary > runStart) {
      runs.push({ startX: runStart * BUCKET_PX, endX: Math.min(boundary * BUCKET_PX, totalWidth), count });
    }
    count += change;
    maxCount = Math.max(maxCount, count);
    runStart = boundary;
  }
  if (runStart < numBuckets) {
    runs.push({ startX: runStart * BUCKET_PX, endX: totalWidth, count });
  }
  return { runs, maxCount, totalWidth };
}

export function buildSummaryPaths(
  runs: readonly SummaryRun[],
  baseY: number,
  maxBarHeight: number,
  maxCount: number,
  totalWidth: number,
  minBarHeight = 0,
): { stroke: string; fill: string } | null {
  if (runs.length === 0) return null;

  const safeMax = Math.max(1, maxCount);
  const minimumHeight = Math.max(0, Math.min(minBarHeight, maxBarHeight));
  const heightFor = (count: number) =>
    minimumHeight + Math.max(0, Math.min(1, count / safeMax)) * (maxBarHeight - minimumHeight);

  const firstRun = runs[0]!;
  const firstY = baseY - heightFor(firstRun.count);
  const baselineY = baseY - heightFor(0);
  // Ease in from the empty ribbon without consuming the outgoing shoulder of
  // a narrow first island or moving any later density boundary.
  const initialRise =
    firstY < baselineY
      ? Math.min(MAX_SHOULDER_PX, Math.max(MIN_SHOULDER_PX, baselineY - firstY), (firstRun.endX - firstRun.startX) / 2)
      : 0;
  const startY = initialRise > 0 ? baselineY : firstY;
  const cmds: string[] =
    initialRise > 0 ? [`C${initialRise / 2} ${startY} ${initialRise / 2} ${firstY} ${initialRise} ${firstY}`] : [];
  for (let i = 0; i < runs.length; i += 1) {
    const run = runs[i]!;
    const yTop = baseY - heightFor(run.count);
    const next = runs[i + 1];
    const nextY = next ? baseY - heightFor(next.count) : baselineY;
    // Small count changes need tighter rounding than full-height transitions;
    // otherwise short runs become broad bumps when the timeline is zoomed out.
    const shoulder = Math.min(MAX_SHOULDER_PX, Math.max(MIN_SHOULDER_PX, Math.abs(nextY - yTop)));

    const leftShoulder = next || yTop < baselineY ? Math.min(shoulder, (run.endX - run.startX) / 2) : 0;
    const plateauEndX = run.endX - leftShoulder;
    cmds.push(`L${plateauEndX} ${yTop}`);

    if (next) {
      // Each side uses its own available space. A short island can borrow a
      // gentle shoulder from its long neighbor without crossing another curve.
      const rightShoulder = Math.min(shoulder, (next.endX - next.startX) / 2);
      const transitionEndX = run.endX + rightShoulder;
      // Center the easing within that span so an asymmetric shoulder does not
      // squeeze its horizontal tangent into a one-pixel, pointed crest.
      const controlX = (plateauEndX + transitionEndX) / 2;
      cmds.push(`C${controlX} ${yTop} ${controlX} ${nextY} ${transitionEndX} ${nextY}`);
    } else if (leftShoulder > 0) {
      // Mirror the initial rise inside the media endpoint. Half of the final
      // run stays available for its incoming shoulder, even for a tiny island.
      const controlX = (plateauEndX + run.endX) / 2;
      cmds.push(`C${controlX} ${yTop} ${controlX} ${baselineY} ${run.endX} ${baselineY}`);
    }
  }

  const inner = cmds.join("");
  const stroke = `M0 ${startY}${inner}`;
  const fill = `M0 ${baseY}L0 ${startY}${inner}L${totalWidth} ${baseY}Z`;

  return { stroke, fill };
}
